// Collapsible version-history list shown inside an EditablePromptCard when
// the parent toggles `versionsOpen`. Lazy-loads history rows the first time
// it opens, lets the user expand a row to preview, and offers a Restore
// button per row.
//
// Slice C of the AI prompting hardening pass — see
// `docs/planning/ai_prompting_suggest_edit.md` "Hardening pass" → "Slice C".

import { useEffect, useState } from "react";
import {
  listOverrideHistory,
  restoreOverrideFromHistory,
  type PromptHistoryDTO,
  type PromptHistorySource,
} from "@/lib/aiPrompting/client";

export type EditablePromptCardVersionsProps = {
  promptKey: string;
  promptLabel: string;
  /** Bumped by the parent each time a publish / revert lands so the list
   * re-fetches and includes the newly-created snapshot. */
  refreshNonce: number;
  /** Called after a successful restore so the parent can refresh its panel
   * data (active_text + override row + history_count). */
  onRestored: () => void;
};

function sourceLabel(source: PromptHistorySource): string {
  if (source === "publish") return "Before publish";
  if (source === "revert") return "Before revert to default";
  if (source === "restore") return "Before restore";
  return "Catalog default";
}

function sourcePillClass(source: PromptHistorySource): string {
  if (source === "catalog") {
    return "border-gray-300 bg-gray-100 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200";
  }
  if (source === "revert") {
    return "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100";
  }
  if (source === "restore") {
    return "border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-100";
  }
  return "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100";
}

function formatStamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function previewLines(text: string, lines = 3): string {
  const split = text.split(/\r?\n/);
  if (split.length <= lines) return text;
  return split.slice(0, lines).join("\n") + "\n…";
}

export function EditablePromptCardVersions({
  promptKey,
  promptLabel,
  refreshNonce,
  onRestored,
}: EditablePromptCardVersionsProps) {
  const [rows, setRows] = useState<PromptHistoryDTO[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The setLoading/setError pair is the intentional "show spinner
    // immediately" pattern; the rest of the writes happen post-await and
    // are not synchronous-in-effect. Same exemption pattern as
    // `src/hooks/useVoiceSettings.ts`.
    /* eslint-disable react-hooks/set-state-in-effect */
    setLoading(true);
    setError(null);
    /* eslint-enable react-hooks/set-state-in-effect */
    void (async () => {
      const r = await listOverrideHistory(promptKey);
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error);
        setRows([]);
      } else {
        setRows(r.data.history);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [promptKey, refreshNonce]);

  const handleRestore = async (row: PromptHistoryDTO) => {
    const confirmMsg =
      `Restore "${promptLabel}" to the version from ${formatStamp(row.created_at)}?\n\n` +
      "The current text will be saved to history first, so you can roll back if needed.";
    if (!window.confirm(confirmMsg)) return;
    setRestoring(row.id);
    setError(null);
    const r = await restoreOverrideFromHistory(promptKey, row.id);
    setRestoring(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onRestored();
  };

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-200">
          Version history
        </h4>
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">Newest first · up to 50 kept</p>
      </div>

      {loading && !rows ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">Loading versions…</p>
      ) : null}
      {error ? (
        <p className="rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {error}
        </p>
      ) : null}
      {!loading && rows && rows.length === 0 && !error ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">No prior versions yet.</p>
      ) : null}

      <ul className="space-y-2">
        {(rows ?? []).map((row) => {
          const isExpanded = expanded === row.id;
          const isRestoring = restoring === row.id;
          return (
            <li
              key={row.id}
              className="rounded-md border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900/70"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={
                      "rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                      sourcePillClass(row.source)
                    }
                  >
                    {sourceLabel(row.source)}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-zinc-300">
                    {formatStamp(row.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : row.id)}
                    className="text-[11px] rounded-md border border-gray-300 bg-white px-2 py-0.5 text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? "Hide" : "Preview"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRestore(row)}
                    disabled={isRestoring}
                    className="text-[11px] rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100"
                  >
                    {isRestoring ? "Restoring…" : "Restore"}
                  </button>
                </div>
              </div>
              {isExpanded ? (
                <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] leading-relaxed text-gray-800 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-200">
                  {row.text}
                </pre>
              ) : (
                <p className="mt-1 line-clamp-3 whitespace-pre-wrap font-mono text-[11px] text-gray-500 dark:text-zinc-400">
                  {previewLines(row.text, 2)}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
